import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './rules-list.html';
import { createRule, listRules, deleteRule, updateRule } from '../services/api.service';
import type { Rule } from '../services/api.types';
import { ConfirmDialog } from '../components/confirm-dialog';

type ExportedRule = Pick<Rule, 'title' | 'selector' | 'code'>;

interface RuleExport {
  format: 'radishdms-rules';
  version: 1;
  exportedAt: string;
  rules: ExportedRule[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

@customElement({ name: 'rules-list', template })
export class RulesList {
  private readonly i18n: I18N = resolve(I18N);

  public rules: Rule[] = [];
  public errorMessage = '';
  public importing = false;
  public showModal = false;
  public ruleToEdit: Rule | null = null;
  public importInput: HTMLInputElement | null = null;

  public async loading(): Promise<void> {
    await this.fetchRules();
  }

  public async fetchRules(): Promise<void> {
    try {
      this.rules = await listRules();
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }

  public openCreateModal(): void {
    this.ruleToEdit = null;
    this.showModal = true;
  }

  public openEditModal(rule: Rule): void {
    this.ruleToEdit = rule;
    this.showModal = true;
  }

  public exportRule(rule: Rule): void {
    const exportData: RuleExport = {
      format: 'radishdms-rules',
      version: 1,
      exportedAt: new Date().toISOString(),
      rules: [{
        title: rule.title,
        selector: rule.selector,
        code: rule.code,
      }],
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = rule.title
        .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
        .trim()
        .slice(0, 80) || 'rule';
      link.download = `radishdms-rule-${safeTitle}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  public openImportPicker(): void {
    this.importInput?.click();
  }

  public async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.importing) return;

    this.errorMessage = '';
    this.importing = true;

    try {
      const importedRules = this.parseRuleExport(await file.text());
      const existingRules = new Map(
        this.rules.map(rule => [this.normalizeTitle(rule.title), rule]),
      );
      const failures: string[] = [];

      for (const importedRule of importedRules) {
        const titleKey = this.normalizeTitle(importedRule.title);
        const existingRule = existingRules.get(titleKey);
        if (existingRule) {
          const confirmed = await ConfirmDialog.confirm(
            this.i18n.tr('rules.import.overwrite', { title: importedRule.title }),
            this.i18n.tr('rules.import.overwriteTitle'),
          );
          if (!confirmed) {
            continue;
          }

          try {
            const updatedRule = await updateRule(existingRule.id, importedRule);
            existingRules.set(titleKey, updatedRule);
          } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            failures.push(`${importedRule.title}: ${message}`);
          }
          continue;
        }

        try {
          const createdRule = await createRule(importedRule);
          existingRules.set(titleKey, createdRule);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : String(error);
          failures.push(`${importedRule.title}: ${message}`);
        }
      }

      await this.fetchRules();
      if (failures.length > 0) {
        await ConfirmDialog.alert(
          `${this.i18n.tr('rules.import.failed', { count: failures.length })} ${failures.join(' ')}`,
          this.i18n.tr('common.error'),
        );
      }
    } catch (error: unknown) {
      await ConfirmDialog.alert(
        error instanceof Error
        ? error.message
        : this.i18n.tr('rules.import.invalidFile'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.importing = false;
    }
  }

  private parseRuleExport(text: string): ExportedRule[] {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      throw new Error(this.i18n.tr('rules.import.invalidFile'));
    }

    const entries = Array.isArray(parsed)
      ? parsed
      : isRecord(parsed) && Array.isArray(parsed.rules)
        ? parsed.rules
        : null;
    if (!entries) throw new Error(this.i18n.tr('rules.import.invalidFile'));
    if (entries.length === 0) throw new Error(this.i18n.tr('rules.import.empty'));

    return entries.map((entry, index) => {
      if (!isRecord(entry) ||
          typeof entry.title !== 'string' ||
          typeof entry.selector !== 'string' ||
          typeof entry.code !== 'string' ||
          !entry.title.trim() ||
          !entry.selector.trim()) {
        throw new Error(this.i18n.tr('rules.import.invalidRule', { index: index + 1 }));
      }
      return {
        title: entry.title.trim(),
        selector: entry.selector.trim(),
        code: entry.code,
      };
    });
  }

  private normalizeTitle(title: string): string {
    return title.trim().toLocaleLowerCase();
  }

  public onModalSaved(rule: Rule): void {
    const idx = this.rules.findIndex(r => r.id === rule.id);
    if (idx >= 0) {
      this.rules[idx] = rule;
      this.rules = [...this.rules];
    } else {
      this.rules = [...this.rules, rule];
    }
    this.showModal = false;
    this.ruleToEdit = null;
  }

  public onModalClosed(): void {
    this.showModal = false;
    this.ruleToEdit = null;
  }

  public async removeRule(rule: Rule): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('rules.delete.confirm', { title: rule.title }),
      this.i18n.tr('rules.delete.confirmTitle')
    );
    if (!confirmed) return;
    try {
      await deleteRule(rule.id);
      this.rules = this.rules.filter(r => r.id !== rule.id);
    } catch (e: unknown) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
    }
  }
}
